import {
  createAutoFillProgram,
} from "./autoFillEngine";
import {
  AUTO_FILL_PROGRAM_ENTRY_AMOUNT,
  getLevelAmount,
} from "../../config/autoFillProgramConfig";
import {
  isAutoFillUpgradeOrder,
  orderQualifiesForAutoFillProgram,
  voucherActivationIncludesAutoFill,
} from "../../utils/autoFillMembershipEnrollment";
import { CURRENT_ACTIVATION_FEE } from "../../config/membershipConfig";
import {
  canSpawnProgramOnWithdrawal,
  computeWithdrawalWalletPayoutAmount,
  isRootAutoFillProgram,
  usesSharedMemberPointsWalletForProgram,
} from "./autoFillProgramSpawn";
import { splitAutoFillWithdrawalAmount } from "../../utils/autoFillCoPointsWallet";
import {
  addDoc,
  collection,
  doc,
  getDocs,
  query,
  updateDoc,
  where,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import {
  countUserWithdrawalsByStatus,
  evaluateAutoFillWithdrawalEligibility,
} from "./autoFillWithdrawalEligibility";
import {
  allocateWithdrawalAcrossLevelStates,
  allocateWithdrawalAtSingleLevel,
  applyWithdrawalLevelForfeitureUpdates,
  hydrateLevelForfeitureStates,
  mergeLevelForfeiturePersistUpdates,
} from "./autoFillLevelForfeitureTracking";
import { computeLevelCoPointsBuckets, sumProgramPointsWithdrawalOutflow } from "./autoFillWithdrawalSummary";
import { computeProgramCoPointsWallet, getWithdrawalCoPointsBreakdown } from "../../utils/autoFillCoPointsWallet";

class AutoFillProgramService {
  static shouldCreateProgramForPackageOrder(order, pkg) {
    return orderQualifiesForAutoFillProgram(order, pkg);
  }

  static resolveEntryAmount() {
    return AUTO_FILL_PROGRAM_ENTRY_AMOUNT;
  }

  static async markAutoFillProgramPurchased(userId) {
    const userSnap = await getDocs(
      query(collection(db, "users"), where("uid", "==", userId))
    );
    if (userSnap.empty) return;
    const now = new Date().toISOString();
    await updateDoc(doc(db, "users", userSnap.docs[0].id), {
      autoFillProgramPurchased: true,
      autoFillProgramPurchasedAt: now,
      purchasedActivationFee: CURRENT_ACTIVATION_FEE,
      updatedAt: now,
    });
  }

  static async enrollIncludedAutoFillForNewMembership({
    userId,
    userEmail,
    userName,
    orderId,
    activationFee,
  }) {
    if (!voucherActivationIncludesAutoFill(activationFee)) {
      return { created: false, reason: "activation_tier_no_afp" };
    }

    if (orderId) {
      const dup = await getDocs(
        query(
          collection(db, "autoFillPrograms"),
          where("sourceOrderId", "==", orderId)
        )
      );
      if (!dup.empty) {
        return { created: false, reason: "already_created_for_order" };
      }
    }

    const existingPrograms = await getDocs(
      query(
        collection(db, "autoFillPrograms"),
        where("userId", "==", userId),
        where("status", "==", "active")
      )
    );
    if (!existingPrograms.empty) {
      await this.markAutoFillProgramPurchased(userId);
      return {
        created: false,
        reason: "already_has_active_program",
        programId: existingPrograms.docs[0].data().programId,
      };
    }

    const packageAmount = AUTO_FILL_PROGRAM_ENTRY_AMOUNT;
    const result = await createAutoFillProgram({
      userId,
      userEmail,
      userName,
      packageAmount,
      orderId: orderId || null,
      parentProgramId: null,
    });

    await this.markAutoFillProgramPurchased(userId);

    return { created: true, ...result };
  }

  static async createProgramFromConfirmedOrder(order, pkg) {
    if (!this.shouldCreateProgramForPackageOrder(order, pkg)) {
      return { created: false, reason: "not_eligible_order_type" };
    }

    if (isAutoFillUpgradeOrder(order, pkg)) {
      const existingPrograms = await getDocs(
        query(
          collection(db, "autoFillPrograms"),
          where("userId", "==", order.buyerId),
          where("status", "==", "active")
        )
      );
      if (!existingPrograms.empty) {
        await this.markAutoFillProgramPurchased(order.buyerId);
        return {
          created: false,
          reason: "already_has_active_program",
          programId: existingPrograms.docs[0].data().programId,
        };
      }
    }

    if (order?.cartOrderId) {
      const dup = await getDocs(
        query(
          collection(db, "autoFillPrograms"),
          where("sourceOrderId", "==", order.cartOrderId)
        )
      );
      if (!dup.empty) {
        return { created: false, reason: "already_created_for_order" };
      }
    }

    const packageAmount = this.resolveEntryAmount(order, pkg);
    const result = await createAutoFillProgram({
      userId: order.buyerId,
      userEmail: order.buyerEmail,
      userName: order.buyerName,
      packageAmount,
      orderId: order.cartOrderId,
      parentProgramId: null,
    });

    await this.markAutoFillProgramPurchased(order.buyerId);

    return { created: true, ...result };
  }

  static async requestPointsWithdrawal(
    userId,
    amount,
    userEmail,
    userName,
    sourceProgramId = null,
    coPointsLevel = null
  ) {
    const userSnap = await getDocs(
      query(collection(db, "users"), where("uid", "==", userId))
    );
    if (userSnap.empty) throw new Error("User not found");

    const userDoc = userSnap.docs[0];
    const userData = userDoc.data();
    const targetLevel =
      coPointsLevel != null ? Number(coPointsLevel) : null;
    const eligibility = await evaluateAutoFillWithdrawalEligibility(
      userData,
      userDoc.id,
      sourceProgramId,
      targetLevel != null ? { withdrawalProgramLevel: targetLevel } : {}
    );
    if (!eligibility.eligible) {
      throw new Error(eligibility.reasons.join(" "));
    }

    const pendingNow = await countUserWithdrawalsByStatus(userId, "PENDING");
    if (pendingNow > 0) {
      throw new Error(
        "You have a pending withdrawal. Wait for admin approval first."
      );
    }

    const points = userData.autoFillPoints || {};
    const co = userData.autoFillCoPoints || {};
    const available =
      (points.available || 0) + (co.available || 0);
    const amt = Number(amount);
    if (amt > available) throw new Error("Insufficient points balance");

    if (targetLevel != null) {
      const expected = getLevelAmount(targetLevel);
      if (Math.abs(amt - expected) > 0.01) {
        throw new Error(
          `Level ${targetLevel} withdrawal must be exactly ₹${expected.toLocaleString("en-IN")}.`
        );
      }
      const progSnap = await getDocs(
        query(
          collection(db, "autoFillPrograms"),
          where("userId", "==", userId),
          where("programId", "==", sourceProgramId || eligibility.programId)
        )
      );
      if (!progSnap.empty) {
        const programDoc = progSnap.docs[0];
        const program = { id: programDoc.id, ...programDoc.data() };
        const ledgerSnap = await getDocs(
          query(collection(db, "autoFillLedger"), where("userId", "==", userId))
        );
        const ledgerRows = ledgerSnap.docs.map((d) => d.data());
        const wdSnap = await getDocs(
          query(
            collection(db, "autoFillWithdrawals"),
            where("userId", "==", userId),
            where("walletType", "==", "POINTS")
          )
        );
        const withdrawalRows = wdSnap.docs.map((d) => d.data());
        const posSnap = await getDocs(
          query(
            collection(db, "autoFillMatrixPositions"),
            where("programDocId", "==", programDoc.id)
          )
        );
        const filledMatrixCountByLevel = {};
        posSnap.docs.forEach((d) => {
          const p = d.data();
          if (p.positionStatus !== "filled") return;
          const L = Number(p.level);
          filledMatrixCountByLevel[L] = (filledMatrixCountByLevel[L] || 0) + 1;
        });
        const includeCo =
          isRootAutoFillProgram(program) &&
          program.enrollmentSeq === 1;
        const walletView = computeProgramCoPointsWallet(
          program,
          ledgerRows,
          userId,
          withdrawalRows,
          {
            includeGenesisAdminCo: includeCo,
            userCoBucket: includeCo ? userData.autoFillCoPoints : null,
            memberPointsWallet: usesSharedMemberPointsWalletForProgram(program)
              ? userData.autoFillPoints
              : null,
            filledMatrixCountByLevel,
          }
        );
        const breakdown = getWithdrawalCoPointsBreakdown(
          userData,
          program,
          walletView
        );
        const isGenesisRoot =
          isRootAutoFillProgram(program) &&
          Number(program.enrollmentSeq) === 1;
        const fifoOutflow = isGenesisRoot
          ? sumProgramPointsWithdrawalOutflow(withdrawalRows, program)
          : {
              withdrawn: breakdown.totalWithdrawn,
              locked: breakdown.totalLocked,
            };
        const buckets = computeLevelCoPointsBuckets(
          program,
          ledgerRows,
          userId,
          {
            withdrawn: fifoOutflow.withdrawn,
            locked: fifoOutflow.locked,
          },
          {
            memberPointsWallet: usesSharedMemberPointsWalletForProgram(program)
              ? userData.autoFillPoints
              : null,
            filledMatrixCountByLevel,
            pointsWithdrawals: withdrawalRows,
          }
        );
        const bucket = buckets.find((b) => b.level === targetLevel);
        if (!bucket || bucket.available < expected - 0.01) {
          throw new Error(
            `No withdrawable CO Points tranche remaining for level ${targetLevel}.`
          );
        }
      }
    }

    const { fromPoints, fromCo } = splitAutoFillWithdrawalAmount(userData, amt);

    const userPatch = {
      autoFillPoints: {
        ...points,
        available: (points.available || 0) - fromPoints,
        locked: (points.locked || 0) + fromPoints,
      },
      updatedAt: new Date().toISOString(),
    };
    if (fromCo > 0 || co.earned || co.available) {
      userPatch.autoFillCoPoints = {
        ...co,
        available: (co.available || 0) - fromCo,
        locked: (co.locked || 0) + fromCo,
        earned: co.earned || 0,
        withdrawn: co.withdrawn || 0,
      };
    }
    await updateDoc(doc(db, "users", userDoc.id), userPatch);

    const withdrawalId = `WD-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
    await addDoc(collection(db, "autoFillWithdrawals"), {
      withdrawalId,
      userId,
      userEmail,
      userName,
      walletType: "POINTS",
      amount: amt,
      lockedFromPoints: fromPoints,
      lockedFromCoPoints: fromCo,
      sourceProgramId: sourceProgramId || eligibility.programId || null,
      coPointsLevel: targetLevel,
      programLevelAtRequest:
        targetLevel != null ? targetLevel : eligibility.programLevel || null,
      withdrawalSequence: eligibility.nextSequence,
      status: "PENDING",
      requestedAt: new Date().toISOString(),
      createdAt: serverTimestamp(),
    });
    return withdrawalId;
  }

  static async creditUserCommissionBalances(userId, amount) {
    const userSnap = await getDocs(
      query(collection(db, "users"), where("uid", "==", userId))
    );
    if (userSnap.empty) return;

    const userDoc = userSnap.docs[0];
    const data = userDoc.data();
    const amt = Number(amount);
    const currentBalance = data.commissionBalance || 0;
    const currentEarned = data.commissionEarned || 0;
    const currentUsed = data.commissionUsed || 0;
    const oldConfirmed = data.confirmedCommissions || 0;
    const oldTotal = data.totalCommissions || 0;

    if (currentBalance === 0 && oldConfirmed > 0) {
      await updateDoc(doc(db, "users", userDoc.id), {
        commissionBalance: oldConfirmed + amt,
        commissionEarned: oldConfirmed + amt,
        commissionUsed: currentUsed,
        confirmedCommissions: oldConfirmed + amt,
        totalCommissions: oldTotal + amt,
        updatedAt: new Date().toISOString(),
      });
      return;
    }

    await updateDoc(doc(db, "users", userDoc.id), {
      commissionBalance: currentBalance + amt,
      commissionEarned: currentEarned + amt,
      confirmedCommissions: oldConfirmed + amt,
      totalCommissions: oldTotal + amt,
      updatedAt: new Date().toISOString(),
    });
  }

  /** Credit Wallet & Points (referral Points balance) when admin approves a CO Points withdrawal. */
  static async creditPointsWalletForWithdrawal(withdrawal, payoutAmount = null) {
    const withdrawalId =
      withdrawal.withdrawalId || withdrawal.id || null;
    const amount = Number(payoutAmount ?? withdrawal.amount);
    const userId = withdrawal.userId;
    if (!withdrawalId || !userId || !amount || amount <= 0) {
      return { credited: false, reason: "invalid_withdrawal" };
    }

    const byPublicId = await getDocs(
      query(
        collection(db, "commissions"),
        where("autoFillWithdrawalId", "==", withdrawalId)
      )
    );
    const byDocId =
      withdrawal.id && withdrawal.withdrawalId !== withdrawal.id
        ? await getDocs(
            query(
              collection(db, "commissions"),
              where("autoFillWithdrawalDocId", "==", withdrawal.id)
            )
          )
        : { empty: true, docs: [] };

    if (!byPublicId.empty || !byDocId.empty) {
      return { credited: false, reason: "already_credited" };
    }

    const now = new Date().toISOString();
    const commissionId = `AF-WD-${withdrawalId}`;

    await addDoc(collection(db, "commissions"), {
      commissionId,
      type: "auto_fill_points_withdrawal",
      autoFillWithdrawalId: withdrawalId,
      autoFillWithdrawalDocId: withdrawal.id || null,
      referrerId: userId,
      referrerName: withdrawal.userName || "User",
      referrerEmail: withdrawal.userEmail || "",
      buyerId: userId,
      buyerName: withdrawal.userName || "User",
      orderType: "auto_fill",
      commissionAmount: amount,
      commissionLevel: 0,
      commissionPercentage: 0,
      orderAmount: amount,
      profitMargin: amount,
      status: "confirmed",
      createdAt: now,
      updatedAt: now,
      confirmedAt: now,
      eligibilityChecked: true,
      referrerPackagePurchaseStatus: true,
      isMultiLevelCommission: true,
      notes:
        Number(payoutAmount ?? withdrawal.amount) !== Number(withdrawal.amount)
          ? `Auto Fill CO Points withdrawal: ₹${amount} to Wallet & Points (₹${withdrawal.amount} tranche; ₹${Number(withdrawal.amount) - amount} funded child AFP spawn).`
          : `Auto Fill CO Points withdrawal paid to Wallet & Points (₹${amount})`,
    });

    await this.creditUserCommissionBalances(userId, amount);
    return { credited: true, commissionId };
  }

  static async onWithdrawalRejected(withdrawal) {
    if (!withdrawal?.userId || withdrawal.status !== "PENDING") return;

    const userSnap = await getDocs(
      query(collection(db, "users"), where("uid", "==", withdrawal.userId))
    );
    if (userSnap.empty) return;

    const userDoc = userSnap.docs[0];
    const userData = userDoc.data();
    const points = userData.autoFillPoints || {};
    const co = userData.autoFillCoPoints || {};
    const amount = Number(withdrawal.amount);
    const fromPoints =
      withdrawal.lockedFromPoints != null
        ? Number(withdrawal.lockedFromPoints)
        : amount;
    const fromCo =
      withdrawal.lockedFromCoPoints != null
        ? Number(withdrawal.lockedFromCoPoints)
        : 0;

    await updateDoc(doc(db, "users", userDoc.id), {
      autoFillPoints: {
        ...points,
        available: (points.available || 0) + fromPoints,
        locked: Math.max(0, (points.locked || 0) - fromPoints),
      },
      autoFillCoPoints: {
        ...co,
        available: (co.available || 0) + fromCo,
        locked: Math.max(0, (co.locked || 0) - fromCo),
      },
      updatedAt: new Date().toISOString(),
    });
  }

  static async onWithdrawalApproved(withdrawal, adminId) {
    if (withdrawal.status !== "PENDING") return;

    const userSnap = await getDocs(
      query(collection(db, "users"), where("uid", "==", withdrawal.userId))
    );
    if (userSnap.empty) return;
    const userDoc = userSnap.docs[0];
    const userData = userDoc.data();
    const points = userData.autoFillPoints || {};
    const co = userData.autoFillCoPoints || {};
    const amount = Number(withdrawal.amount);
    const fromPoints =
      withdrawal.lockedFromPoints != null
        ? Number(withdrawal.lockedFromPoints)
        : amount;
    const fromCo =
      withdrawal.lockedFromCoPoints != null
        ? Number(withdrawal.lockedFromCoPoints)
        : 0;

    const progSnap = await getDocs(
      query(
        collection(db, "autoFillPrograms"),
        where("userId", "==", withdrawal.userId)
      )
    );
    const allPrograms = progSnap.docs.map((d) => ({
      docId: d.id,
      ...d.data(),
    }));

    let programDoc = null;
    let program = null;
    if (withdrawal.sourceProgramId) {
      const match = progSnap.docs.find(
        (d) => d.data().programId === withdrawal.sourceProgramId
      );
      if (match) {
        programDoc = match;
        program = match.data();
      }
    }
    if (!programDoc) {
      const active = progSnap.docs
        .map((d) => ({ ref: d, data: d.data() }))
        .filter((r) => ["active", "on_hold"].includes(r.data.status))
        .sort(
          (a, b) =>
            (Number(a.data.enrollmentSeq) || 0) -
            (Number(b.data.enrollmentSeq) || 0)
        );
      const rootRow =
        active.find((r) => !r.data.parentProgramId) || active[0];
      if (rootRow) {
        programDoc = rootRow.ref;
        program = rootRow.data;
      }
    }

    const payoutPlan = computeWithdrawalWalletPayoutAmount(
      withdrawal,
      program,
      allPrograms
    );

    await updateDoc(doc(db, "users", userDoc.id), {
      autoFillPoints: {
        ...points,
        locked: Math.max(0, (points.locked || 0) - fromPoints),
        withdrawn: (points.withdrawn || 0) + fromPoints,
      },
      autoFillCoPoints: {
        ...co,
        locked: Math.max(0, (co.locked || 0) - fromCo),
        withdrawn: (co.withdrawn || 0) + fromCo,
      },
      updatedAt: new Date().toISOString(),
    });

    await this.creditPointsWalletForWithdrawal(
      withdrawal,
      payoutPlan.walletPayout
    );

    const level = program?.currentLevel || 1;

    let levelForfeitureApplied = {};
    if (programDoc && program) {
      const ledgerSnap = await getDocs(
        query(
          collection(db, "autoFillLedger"),
          where("userId", "==", withdrawal.userId)
        )
      );
      const ledgerRows = ledgerSnap.docs.map((d) => d.data());
      const programWithIds = {
        ...program,
        programId: program.programId,
        userId: withdrawal.userId,
      };
      const hydrated = hydrateLevelForfeitureStates(programWithIds, ledgerRows);
      const coLevel =
        withdrawal.coPointsLevel != null
          ? Number(withdrawal.coPointsLevel)
          : null;
      const allocation =
        coLevel != null
          ? allocateWithdrawalAtSingleLevel(hydrated, coLevel, amount)
          : allocateWithdrawalAcrossLevelStates(hydrated, level, amount);
      const { levelUpdates, appliedByLevel } = allocation;
      levelForfeitureApplied = appliedByLevel;
      const persistUpdates = mergeLevelForfeiturePersistUpdates(
        programWithIds,
        hydrated,
        levelUpdates,
        level
      );
      if (Object.keys(persistUpdates).length > 0) {
        await applyWithdrawalLevelForfeitureUpdates(
          programDoc.id,
          program,
          persistUpdates
        );
      }
    }
    const level6AmountApplied = levelForfeitureApplied[6] || 0;

    let spawnedProgramId = null;
    if (program && isRootAutoFillProgram(program) && payoutPlan.willSpawn) {
      const spawnCheck = canSpawnProgramOnWithdrawal(
        program,
        allPrograms,
        withdrawal
      );
      if (spawnCheck.allowed) {
        const spawn = await createAutoFillProgram({
          userId: withdrawal.userId,
          userEmail: withdrawal.userEmail,
          userName: withdrawal.userName,
          packageAmount: AUTO_FILL_PROGRAM_ENTRY_AMOUNT,
          orderId: `SPAWN-WD-${withdrawal.withdrawalId}`,
          parentProgramId: program.programId,
          spawnedFromWithdrawal: true,
          spawnedFromWithdrawalId: withdrawal.withdrawalId,
          skipActiveProgramPointerUpdate: true,
        });
        spawnedProgramId = spawn.programId;
        await updateDoc(doc(db, "autoFillPrograms", programDoc.id), {
          withdrawalSpawnCount: spawnCheck.currentCount + 1,
          updatedAt: new Date().toISOString(),
        });
      }
    }

    const withdrawalPatch = {
      status: "COMPLETED",
      approvedBy: adminId,
      approvedAt: new Date().toISOString(),
      completedAt: new Date().toISOString(),
      walletPayoutAmount: payoutPlan.walletPayout,
    };
    if (payoutPlan.spawnEntryAmount > 0) {
      withdrawalPatch.spawnFundAmount = payoutPlan.spawnEntryAmount;
    }
    if (spawnedProgramId) {
      withdrawalPatch.spawnedProgramId = spawnedProgramId;
    }
    if (level6AmountApplied > 0) {
      withdrawalPatch.level6AmountApplied = level6AmountApplied;
    }
    if (Object.keys(levelForfeitureApplied).length > 0) {
      withdrawalPatch.levelForfeitureApplied = levelForfeitureApplied;
    }
    await updateDoc(doc(db, "autoFillWithdrawals", withdrawal.id), withdrawalPatch);
  }
}

export default AutoFillProgramService;
